/**
 * V70 게임대회 — 게임센터
 *
 * - PC: 2열 (타자왕 세트 | 마블 세트). 각 세트 = 카드 + 리더보드 TOP10
 * - 모바일: 1열 자동 전환 — 타자왕 카드 → 타자왕 리더보드 → 마블 카드 → 마블 리더보드
 * - 두 게임의 리더보드 데이터 / state / 조회 실패 완전 분리 (Promise.allSettled)
 * - 각 게임별 leaderboardVisible 정책 독립 적용 (OFF/오픈 전 숨김)
 * - 마블 리더보드는 UID 로딩 완료 후 조회 (useCurrentUser 사용)
 * - Phase 2 Mock 범위 안내 (본 브라우저 범위, 공식 순위 아님) — 전체 직원 순위 오인 금지
 * - 서버 API / 라우트 / 점수 계산 규칙 변경 없음
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Play, Lock, Clock, Zap, Dices, ChevronRight, AlertTriangle, RefreshCw, Users, Hash, BookOpen, Eye } from 'lucide-react';
// V70G6E — 엔글 오목 경기 규칙 모달
import { OmokRulesModal } from './omok/OmokRulesModal';
import { acidRainApi } from './acidRain/serverApi';
import type { AcidRainSettings, DailyCoinStatus, LeaderboardRow } from './acidRain/types';
import { useCurrentUser } from '../../shared/hooks/useCurrentUser';
import { useOrgUnitsPublic } from '../../shared/hooks/useOrgUnitsPublic';
import { resolveLeaderboardOrgName, buildOrgMap } from './acidRain/orgLabel';
import { marbleApi } from './marble/mock/serverApi';
import type { LeaderboardRow as MarbleLeaderboardRow, DailyDiceStatus as MarbleDailyDiceStatus } from './marble/engine/types';
import { computeGamePeriod, periodBadge, formatDiff, ALWAYS_OPEN_BADGE, type GamePeriodResult, type GamePeriodState } from './shared/gamePeriod';
// V70-J — 게임대회 공통 헤더
import { TournamentPageHeader, tournamentPageStyle } from './shared/TournamentPageHeader';
// V70-M — 게임대회 다크 테마 (게임 카드 3개의 고유 그라데이션·일러스트는 그대로)
import { DARK, DARK_EMPTY_CELL, darkTintBadge, darkCard, darkErrorBox } from './shared/tournamentDarkTheme';
// ISSUE-01 재제보: 리더보드 UI 에서 Firestore users.nameKo 로 override (기존 저장 레코드 '이름 없음' 극복)
import { resolveUserNames, resolveDisplayName } from './shared/userNameResolver';
// V70 디자인 고급화 — 중앙 디자인 토큰 (타자왕: 차콜/블루그레이/골드)
// (마블은 2026-10-19 NGLE 브랜드 Teal 재디자인으로 직접 리터럴 사용)
import { ACIDRAIN_COLORS } from './shared/designTokens';
// V70 2026-10-19 — 마블 서버 전환 안내 분기
import { isPhase3cServerEnabled } from './shared/phase3cFlag';
import { ADMIN_PREVIEW_LEADERBOARD_NOTICE } from './shared/leaderboardPolicy';
import { buildLeaderboardSlots, LEADERBOARD_EMPTY_CELL } from './shared/leaderboardSlots';

export function TournamentGameCenterPage() {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { orgUnits } = useOrgUnitsPublic();
  const orgMap = useMemo(() => buildOrgMap(orgUnits || []), [orgUnits]);

  // V70G6E — 엔글 오목 경기 규칙 모달
  const [omokRulesOpen, setOmokRulesOpen] = useState<boolean>(false);

  // 설정/코인 (게임 카드용)
  const [settings, setSettings] = useState<AcidRainSettings | null>(null);
  const [coin, setCoin] = useState<DailyCoinStatus | null>(null);
  const [acidPeriod, setAcidPeriod] = useState<GamePeriodResult | null>(null);
  const [marblePeriod, setMarblePeriod] = useState<GamePeriodResult | null>(null);
  // V70G5A Bug 3 — 마블 일일 주사위 잔여 상태 (타자왕 'coin' 과 동일 패턴).
  //   limited 모드에선 remaining/limit, unlimited 모드에선 '무제한'. 소진 시 '주사위 소진'.
  const [marbleDice, setMarbleDice] = useState<MarbleDailyDiceStatus | null>(null);
  const [loading, setLoading] = useState(true);

  // 타자왕 리더보드 — 독립 state
  const [acidTop10, setAcidTop10] = useState<LeaderboardRow[] | null>(null);
  const [acidLbLoading, setAcidLbLoading] = useState(true);
  const [acidLbError, setAcidLbError] = useState<string | null>(null);
  // ISSUE-01 재제보: Top 10 UID → Firestore nameKo map (fail-open 빈 Map 가능)
  const [acidNameMap, setAcidNameMap] = useState<Map<string, string>>(new Map());

  // 마블 리더보드 — 독립 state (UID 준비 후 조회)
  const [marbleTop10, setMarbleTop10] = useState<MarbleLeaderboardRow[] | null>(null);
  const [marbleLbLoading, setMarbleLbLoading] = useState(true);
  const [marbleLbError, setMarbleLbError] = useState<string | null>(null);

  // ── 게임 설정 로드 (각자 격리) ──
  //   V70G5A Bug 3 — 마블 주사위 상태(getDailyDiceStatus) 는 user.profile.id 필요 → user 준비 후 조회.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const results = await Promise.allSettled([
        acidRainApi.getSettings(),
        acidRainApi.getDailyCoinStatus(),
        marbleApi.getSettings(),
      ]);
      if (cancelled) return;
      const [sR, cR, marbleR] = results;
      if (sR.status === 'fulfilled') {
        setSettings(sR.value);
        setAcidPeriod(computeGamePeriod({
          enabled: sR.value.enabled,
          openStartAt: sR.value.openStartAt ?? null,
          openEndAt: sR.value.openEndAt ?? null,
        }));
      }
      if (cR.status === 'fulfilled') setCoin(cR.value);
      if (marbleR.status === 'fulfilled') {
        setMarblePeriod(computeGamePeriod({
          enabled: marbleR.value.enabled,
          openStartAt: marbleR.value.eventStartAt,
          openEndAt: marbleR.value.eventEndAt,
        }));
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // V70G5A Bug 3 — 마블 일일 주사위 잔여 상태 조회 (user 준비 후).
  //   실패해도 카드 노출은 유지 (fail-open) — 상태 null 일 경우 '…' 표시.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const s = await marbleApi.getDailyDiceStatus(user.profile.id);
        if (!cancelled) setMarbleDice(s);
      } catch {
        /* fail-open — remaining 상태를 모르면 '…' 로 표시 */
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // ── 타자왕 리더보드 로더 (재시도 가능) ──
  const loadAcidLeaderboard = useCallback(async () => {
    setAcidLbLoading(true);
    setAcidLbError(null);
    try {
      const l = await acidRainApi.getLeaderboard();
      // V70-C — 점수순 재정렬 후 상위 10명 (표시 시에도 같은 규칙으로 1~10위 슬롯 구성)
      const top = buildLeaderboardSlots(l, (r) => r.uid, (r) => r.bestScore, true).flatMap((s) => (s.row ? [s.row] : []));
      setAcidTop10(top);
      // ISSUE-01 재제보: Top 10 UID 로 Firestore nameKo 조회 (demo-* skip, 최대 10 getDoc)
      try {
        const map = await resolveUserNames(top.map((r) => r.uid));
        setAcidNameMap(map);
      } catch {
        /* fail-open — 조회 실패 시 저장된 displayName fallback */
      }
    } catch (err) {
      setAcidLbError((err as Error)?.message ?? String(err));
    } finally {
      setAcidLbLoading(false);
    }
  }, []);

  useEffect(() => { loadAcidLeaderboard(); }, [loadAcidLeaderboard]);

  // ── 마블 리더보드 로더 (UID 준비 후, 재시도 가능) ──
  const loadMarbleLeaderboard = useCallback(async () => {
    if (!user) return;
    setMarbleLbLoading(true);
    setMarbleLbError(null);
    try {
      const lb = await marbleApi.getLeaderboard(user.profile.id);
      setMarbleTop10(lb.rows);
    } catch (err) {
      setMarbleLbError((err as Error)?.message ?? String(err));
    } finally {
      setMarbleLbLoading(false);
    }
  }, [user]);

  useEffect(() => { if (user) loadMarbleLeaderboard(); }, [user, loadMarbleLeaderboard]);

  const enabled = settings?.enabled ?? false;
  const isUnlimited = coin?.mode === 'unlimited';
  const remaining = coin?.remaining ?? 0;
  const acidPlayable = acidPeriod?.canPlay && enabled && (isUnlimited || remaining > 0);
  const acidOutsidePeriod = acidPeriod?.state === 'before' || acidPeriod?.state === 'after';

  // V70G5A Bug 3 — 마블 주사위 잔여에 따른 플레이 가능 여부 + 상태 라벨/값.
  //   unlimited: 무제한. limited: remaining > 0 때만 플레이 가능.
  //   표시 (타자왕 '오늘 남은 도전' 포맷과 일관): '무제한' / 'N / limit' / '주사위 소진'.
  const marbleIsUnlimited = marbleDice?.mode === 'unlimited';
  const marbleRemaining = marbleDice?.remaining ?? 0;
  const marbleLimit = marbleDice?.limit ?? null;
  const marbleDiceAvailable = marbleIsUnlimited || marbleRemaining > 0;
  const marblePlayable = (marblePeriod?.canPlay ?? false) && (marbleDice == null || marbleDiceAvailable);
  // V70G8D — 주말(KST 토·일)은 신규 지급 없이 금요일 잔여분만 사용
  const marbleWeekend = !marbleIsUnlimited && marbleDice?.weekendCarryover === true;
  const marbleStatValue = marbleDice == null
    ? '…'
    : marbleIsUnlimited
      ? '무제한'
      : marbleRemaining > 0
        ? `${marbleRemaining} / ${marbleLimit ?? '-'}${marbleWeekend ? ' (금요일 잔여)' : ''}`
        : marbleWeekend ? '주말 지급 없음' : '주사위 소진';
  const marbleBlockedDuringLabel = (marblePeriod?.canPlay && marbleDice != null && !marbleIsUnlimited && marbleRemaining <= 0)
    ? (marbleWeekend ? '주말 지급 없음 · 월요일 재개' : '주사위 소진')
    : undefined;

  // 각 게임별 리더보드 노출 정책 (OFF/오픈 전 → 숨김)
  //   V70-C — system_admin 은 비공개(OFF 등) 상태에서도 리더보드 틀을 "관리자 미리보기"로 확인 가능.
  //   일반 직원은 기존대로 숨김 (게임대회 OFF 시에는 라우트 가드에서 먼저 차단).
  const isSystemAdmin = !!user?.roles?.includes('system_admin');
  const acidLbPublic = acidPeriod?.leaderboardVisible ?? false;
  const marbleLbPublic = marblePeriod?.leaderboardVisible ?? false;
  const acidLbVisible = acidLbPublic || (isSystemAdmin && acidPeriod != null);
  const marbleLbVisible = marbleLbPublic || (isSystemAdmin && marblePeriod != null);
  const officialSource = isPhase3cServerEnabled();

  return (
    // V70-K — 게임대회 전용 네이비 배경 (본문 영역 끝까지, 카드 내부 배경은 그대로)
    <div style={tournamentPageStyle({ x: 24, y: 24 })}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        {/* V70 2026-10-19 — 타자왕은 실제 서버 저장 적용, 마블은 각 섹션에서 로컬 안내 */}
        {/* V70-J — e스포츠 공통 헤더 (기존 텍스트 제목·안내 대체) */}
        <TournamentPageHeader page="games" />


        {/* V70G6F — 상단 게임 카드 3열 (리더보드 분리) */}
        <div style={cardsGrid} data-testid="cards-grid-top">
          {/* 엔글 타자왕 카드 */}
          <GameCard
            theme="cyan"
            badge="FEATURED"
            titleAccent="타자왕"
            illustration={<AcidRainIllust />}
            summary="낙하하는 한글 단어를 바닥에 닿기 전에 타이핑하세요. 콤보, 난이도 상승, 생명력 — 아케이드 산성비 타이핑 게임."
            metaChips={[
              { icon: <Clock size={13} />, label: '120초' },
              { icon: <Zap size={13} />, label: '난이도 상승' },
              { icon: <Trophy size={13} />, label: '리더보드' },
            ]}
            statLabel="오늘 남은 도전"
            statValue={loading ? '…' : isUnlimited ? '무제한' : `${remaining}/${coin?.limit ?? '-'}`}
            statActive={!!acidPlayable}
            period={acidPeriod}
            canPlay={!!acidPlayable}
            blockedDuringLabel={enabled && !isUnlimited && remaining <= 0 ? '내일 재도전' : undefined}
            // V70G8I — 도전 횟수 소진 시에도 연습 게임은 가능하므로 상세 화면 진입 버튼 제공
            // V70-I — 대회 기간 전·후에도 연습 게임 진입 제공 (게임대회 OFF=disabled 는 제외)
            blockedViewAction={enabled && (acidOutsidePeriod || (!isUnlimited && remaining <= 0))
              ? { label: '연습 게임', onClick: () => navigate('/tournament/game/acid-rain') }
              : undefined}
            blockedViewStates={ACID_PRACTICE_VIEW_STATES}
            compactActions
            onPlay={() => navigate('/tournament/game/acid-rain')}
            onLeaderboard={() => navigate('/tournament/leaderboard')}
          />

          {/* 엔글 마블 카드 */}
          <GameCard
            theme="violet"
            badge="NEW"
            titleAccent="마블"
            illustration={<MarbleIllust />}
            summary="24칸 보드를 돌며 랜덤 이벤트에 도전하세요. 운과 전략으로 고득점을 노려보는 사내 보드 게임."
            metaChips={[
              { icon: <Dices size={13} />, label: '주사위 3개' },
              { icon: <Zap size={13} />, label: '이벤트 14종' },
              { icon: <Trophy size={13} />, label: '리더보드' },
            ]}
            statLabel="오늘 남은 주사위"
            statValue={marbleStatValue}
            statActive={marblePlayable}
            // V70-P — 밝은 청록 카드 위 가독성: 설명·잔여 수량·소진 모두 기본 텍스트 #F5F6FA
            //   (보조 #AEB8C8 은 이 배경에서 최대 3.0:1 로 부족. 이전 #94a3b8 2.2:1 / 소진 #64748b 1.0:1 / 수량 금색 3.1:1)
            statColors={MARBLE_STAT_COLORS}
            period={marblePeriod}
            canPlay={marblePlayable}
            blockedDuringLabel={marbleBlockedDuringLabel}
            blockedViewAction={marbleBlockedDuringLabel
              // V70-O — 라벨 '마블 화면 보기' → '보기' (동작 동일: 마블 화면 이동)
              ? { label: '보기', onClick: () => navigate('/tournament/game/marble') }
              : undefined}
            onPlay={() => navigate('/tournament/game/marble')}
            onLeaderboard={() => navigate('/tournament/leaderboard-marble')}
          />

          {/* V70G6B — 엔글 오목 (실시간 1:1 대전) + V70G6E — 경기 규칙 버튼 (리더보드 없음) */}
          <GameCard
            theme="omok"
            badge="NEW"
            titleAccent="오목"
            illustration={<OmokIllust />}
            summary="방 코드를 공유해 실시간 1:1 대전. 15x15 보드, 서버 랜덤 흑백 배정 + 렌주룰."
            metaChips={[
              { icon: <Users size={13} />, label: '1:1 실시간' },
              { icon: <Hash size={13} />, label: '6자리 방 코드' },
              { icon: <Trophy size={13} />, label: '렌주룰' },
            ]}
            statLabel="진영"
            statValue="랜덤 배정"
            statActive={true}
            period={null}
            canPlay={true}
            secondaryAction={{ label: '경기 규칙', onClick: () => setOmokRulesOpen(true), icon: 'rules' }}
            onPlay={() => navigate('/tournament/game/omok')}
            onLeaderboard={() => { /* V70G6E — 오목은 리더보드 없음. secondaryAction 으로 대체. */ }}
          />
        </div>

        {/* V70G6F — 하단 리더보드 2열 (타자왕 50% + 마블 50%). 오목은 리더보드 없음. */}
        {(acidLbVisible || marbleLbVisible) && (
          <div style={leaderboardsGrid} data-testid="leaderboards-grid">
            {acidLbVisible && (
              <LeaderboardMiniSection
                title="엔글 타자왕 TOP 10"
                adminPreview={!acidLbPublic}
                officialSource={officialSource}
                loading={acidLbLoading}
                error={acidLbError}
                onRetry={loadAcidLeaderboard}
                onSeeMore={() => navigate('/tournament/leaderboard')}
                rows={acidTop10 == null ? null : acidTop10.map((r) => ({
                  rank: r.rank,
                  uid: r.uid,
                  displayName: resolveDisplayName(r.uid, r.displayName, acidNameMap),
                  orgUnitId: r.orgUnitId,
                  score: r.bestScore,
                }))}
                orgMap={orgMap}
                scoreLabel="최고 점수"
              />
            )}
            {marbleLbVisible && (
              <LeaderboardMiniSection
                title="엔글 마블 TOP 10"
                adminPreview={!marbleLbPublic}
                officialSource={officialSource}
                loading={marbleLbLoading || !user}
                error={marbleLbError}
                onRetry={loadMarbleLeaderboard}
                onSeeMore={() => navigate('/tournament/leaderboard-marble')}
                rows={marbleTop10 == null ? null : marbleTop10.map((r) => ({
                  rank: r.rank, uid: r.uid, displayName: r.displayName, orgUnitId: r.orgUnitId, score: r.totalScore,
                }))}
                orgMap={orgMap}
                scoreLabel="누적 점수"
              />
            )}
          </div>
        )}
      </div>
      {/* V70G6E — 엔글 오목 경기 규칙 모달 */}
      {omokRulesOpen && <OmokRulesModal theme="dark" onClose={() => setOmokRulesOpen(false)} />}
    </div>
  );
}

// ──────── 리더보드 미니 섹션 (공통) ────────

interface LeaderboardRowView {
  rank: number;
  uid: string;
  displayName: string;
  orgUnitId?: string;
  score: number;
}

interface LeaderboardMiniSectionProps {
  title: string;
  /** V70-C — 일반 직원에게 비공개인 상태를 system_admin 이 보는 중 */
  adminPreview: boolean;
  /** V70-C — 공식(서버) 기록 출처 여부. false 면 행이 있어도 순위에 넣지 않음 */
  officialSource: boolean;
  loading: boolean;
  error: string | null;
  rows: LeaderboardRowView[] | null;
  orgMap: Map<string, { id: string; name: string; status?: string }>;
  scoreLabel: string;
  onRetry: () => void;
  onSeeMore: () => void;
}

function LeaderboardMiniSection(p: LeaderboardMiniSectionProps) {
  const slots = buildLeaderboardSlots(p.rows, (r) => r.uid, (r) => r.score, p.officialSource);
  return (
    <div style={lbSectionStyle} data-testid={`lb-${p.title}`}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: DARK.text }}>
          <Trophy size={14} style={{ marginRight: 6, verticalAlign: 'middle', color: '#f59e0b' }} />
          {p.title}
        </h3>
        <button style={seeMoreBtnStyle} onClick={p.onSeeMore}>전체 보기</button>
      </div>

      {p.adminPreview && (
        <div style={adminPreviewStyle} data-testid="leaderboard-admin-preview">
          <Eye size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          {ADMIN_PREVIEW_LEADERBOARD_NOTICE}
        </div>
      )}

      {p.loading && <div style={{ padding: 20, color: DARK.textMuted, textAlign: 'center' }}>로딩 중…</div>}

      {!p.loading && p.error && (
        <div style={errorBoxStyle}>
          <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          리더보드를 불러오지 못했습니다. ({p.error})
          <button style={retryBtnStyle} onClick={p.onRetry}>
            <RefreshCw size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} /> 다시 시도
          </button>
        </div>
      )}

      {/* V70-C — 1~10위 행 항상 표시. 유효 기록이 없는 순위는 "—" */}
      {!p.loading && !p.error && (
        // V70G6F — 긴 텍스트 줄바꿈 방지. table-layout fixed + 각 셀 ellipsis.
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '12%' }} />
            <col style={{ width: '38%' }} />
            <col style={{ width: '28%' }} />
            <col style={{ width: '22%' }} />
          </colgroup>
          <thead>
            <tr>
              <th style={lbThStyle}>순위</th>
              <th style={lbThStyle}>이름</th>
              <th style={lbThStyle}>소속</th>
              <th style={{ ...lbThStyle, textAlign: 'right' }}>{p.scoreLabel}</th>
            </tr>
          </thead>
          <tbody>
            {slots.map(({ rank, row: r }) => {
              if (!r) {
                return (
                  <tr key={`empty-${rank}`} data-testid="lb-empty-row">
                    <td style={{ ...lbTdStyle, fontWeight: 700, color: DARK_EMPTY_CELL }}>{rank}</td>
                    <td style={{ ...lbTdStyle, color: DARK_EMPTY_CELL }}>{LEADERBOARD_EMPTY_CELL}</td>
                    <td style={{ ...lbTdStyle, color: DARK_EMPTY_CELL }}>{LEADERBOARD_EMPTY_CELL}</td>
                    <td style={{ ...lbTdStyle, textAlign: 'right', color: DARK_EMPTY_CELL }}>{LEADERBOARD_EMPTY_CELL}</td>
                  </tr>
                );
              }
              const org = resolveLeaderboardOrgName({ uid: r.uid, orgUnitId: r.orgUnitId }, p.orgMap as never);
              // V70-M — 소속 배지: 종류별 의미 색 유지 (다크 틴트)
              const tint = (hex: string) => { const t = darkTintBadge(hex); return { bg: String(t.background), fg: String(t.color) }; };
              const colorMap: Record<typeof org.kind, { bg: string; fg: string }> = {
                sample: tint('#f59e0b'),
                org: tint('#0ea5e9'),
                unassigned: tint('#6b7280'),
                archived: tint('#dc2626'),
                unknown: tint('#6b7280'),
              };
              const c = colorMap[org.kind];
              return (
                <tr key={r.uid} data-testid="lb-record-row">
                  <td style={{ ...lbTdStyle, fontWeight: 700, color: rank <= 3 ? DARK.gold : DARK.textMuted }}>{rank}</td>
                  <td
                    style={{ ...lbTdStyle, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={r.displayName}
                  >{r.displayName}</td>
                  <td style={{ ...lbTdStyle, overflow: 'hidden' }}>
                    <span
                      style={{
                        padding: '2px 8px', borderRadius: 10, background: c.bg, color: c.fg, fontSize: 11, fontWeight: 600,
                        display: 'inline-block', maxWidth: '100%',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        verticalAlign: 'middle',
                      }}
                      title={org.text}
                    >
                      {org.text}
                    </span>
                  </td>
                  <td style={{ ...lbTdStyle, textAlign: 'right', fontWeight: 700, color: DARK.text, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', whiteSpace: 'nowrap' }}>
                    {r.score.toLocaleString()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ──────── 게임 카드 ────────

const DEFAULT_BLOCKED_VIEW_STATES: GamePeriodState[] = ['during'];
// V70-J — 타자왕 카드 버튼 줄 컴팩트 배치 ([게임 시작/오픈 전/종료됨] · [연습 게임] · [리더보드]).
//   실측(Chromium, Segoe UI, AppShell 사이드바 280px 기준): 3버튼은 카드 안쪽 약 297px(PC 1440) 이상에서 한 줄,
//   그보다 좁으면 넘치지 않고 다음 줄로 내려감. [리더보드]는 모든 폭에서 한 줄. [연습 게임]은 폭·글자 소폭 축소.
//   [연습 게임]이 없을 때(진행 중·도전 남음)는 [게임 시작]이 기존처럼 더 넓게(2:1) 차지.
export const COMPACT_ROW: React.CSSProperties = { flexWrap: 'wrap', gap: 6 };
export const COMPACT_PLAY: React.CSSProperties = { flex: '1.2 1 auto', padding: '12px 8px' };
export const COMPACT_PLAY_SOLO: React.CSSProperties = { flex: '2 1 auto', padding: '12px 12px' };
export const COMPACT_VIEW: React.CSSProperties = { flex: '0.6 1 auto', padding: '12px 8px', fontSize: 13, gap: 4 };
export const COMPACT_LEADERBOARD: React.CSSProperties = { flex: '1 1 auto', padding: '12px 8px', gap: 3, whiteSpace: 'nowrap' };
// V70-O — 소진 상태 묶음 영역: 기존 게임시작 버튼과 같은 몫을 공유, 내부 두 버튼은 영역 안에서만 줄어듦.
//   게임시작 버튼(border-box, 좌우 패딩 16+16)은 flex 기준 크기가 패딩 32px 이므로, 묶음 영역도 같은 기준(2 1 32px)을 써야
//   잔여 상태와 소진 상태의 영역 폭·리더보드 위치가 정확히 같아진다.
const PLAY_BTN_PAD_X = 16;
export const SPLIT_PLAY_AREA: React.CSSProperties = { flex: `2 1 ${PLAY_BTN_PAD_X * 2}px`, minWidth: 0, display: 'flex', gap: 6 };
export const SPLIT_BLOCKED: React.CSSProperties = { flex: '1.5 1 0', minWidth: 0, padding: '12px 6px', fontSize: 13, overflow: 'hidden' };
export const SPLIT_VIEW: React.CSSProperties = { flex: '1 1 0', minWidth: 0, padding: '12px 6px', fontSize: 13, overflow: 'hidden' };
const ELLIPSIS: React.CSSProperties = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' };
/** V70-O — 소진 상태에서만 묶음 영역을 만든다 (그 외 카드·상태의 DOM 구조는 기존 그대로) */
function PlayArea({ split, children }: { split: boolean; children: React.ReactNode }) {
  return split ? <div data-testid="game-card-play-area" style={SPLIT_PLAY_AREA}>{children}</div> : <>{children}</>;
}
// V70-P — 마블 카드 상태 줄 글자색 (V70-M 기본 텍스트)
export const MARBLE_STAT_COLORS = { label: DARK.text, value: DARK.text };
// V70-I — 타자왕 연습 게임 진입: 대회 기간 전·중·후 (disabled=게임대회 OFF 는 제외)
const ACID_PRACTICE_VIEW_STATES: GamePeriodState[] = ['before', 'during', 'after'];

interface GameCardProps {
  theme: 'cyan' | 'violet' | 'omok';
  badge: string;
  titleAccent: string;
  illustration: React.ReactNode;
  summary: string;
  metaChips: Array<{ icon: React.ReactNode; label: string }>;
  statLabel: string;
  statValue: string;
  statActive: boolean;
  period: GamePeriodResult | null;
  canPlay: boolean;
  // ISSUE-02 재제보: 공개 상태(during)인데 플레이 불가 — 코인/도전 횟수 소진 등 — 때 사용할 버튼 라벨.
  //   호출부가 명시한 경우에만 '내일 재도전' 등 전용 문구 표시. 미명시 시 기존 fallback('비공개')은 피하고
  //   period.state 가 'during' 이면 '잠시 후 다시' 등 중립 문구 반환하도록 처리 (아래 로직 참조).
  blockedDuringLabel?: string;
  blockedDuringIcon?: React.ReactNode;
  // V70G6E — 리더보드 대신 쓰일 secondary 액션 (예: 오목 "경기 규칙"). 지정 시 리더보드 버튼 대체.
  secondaryAction?: { label: string; onClick: () => void; icon?: 'rules' | 'trophy' };
  // 공개 중(during)인데 플레이 불가일 때 막힌 버튼 옆에 노출할 조회 전용 진입 (예: 마블 "마블 화면 보기")
  blockedViewAction?: { label: string; onClick: () => void };
  /** V70-I — blockedViewAction 을 노출할 기간 상태 (기본: 진행 중만). 타자왕 연습은 전·중·후 */
  blockedViewStates?: GamePeriodState[];
  /** V70-J — 버튼 줄 컴팩트 배치 (타자왕 전용): [리더보드] 항상 한 줄, [연습 게임] 축소, 좁으면 넘치지 않고 줄바꿈 */
  compactActions?: boolean;
  /** V70-P — 상태 줄(설명·값) 글자색 지정 (마블 전용). 미지정 카드는 기존 색 그대로 */
  statColors?: { label: string; value: string };
  onPlay: () => void;
  onLeaderboard: () => void;
}

export function GameCard(p: GameCardProps) {
  // V70 디자인 고급화 — 네온/과도한 글로우 제거, 톤 다운.
  // cyan = AcidRain (차콜/블루그레이/샴페인 골드), violet = Marble (딥 네이비/샌드/세이지).
  const themeMap = {
    cyan: {
      gradient: `linear-gradient(135deg, ${ACIDRAIN_COLORS.bgPrimary} 0%, ${ACIDRAIN_COLORS.panel} 100%)`,
      border: ACIDRAIN_COLORS.borderStrong,
      accent: ACIDRAIN_COLORS.gold,
      badgeColor: ACIDRAIN_COLORS.goldSoft,
      playBtn: ACIDRAIN_COLORS.gold,
      playFg: ACIDRAIN_COLORS.textOnGold,
      shadow: '0 6px 18px rgba(0, 0, 0, 0.35)',
      titleAccent: ACIDRAIN_COLORS.goldSoft,
    },
    violet /* Marble — NGLE 브랜드 Teal 재디자인 (2026-10-19) */: {
      gradient: 'linear-gradient(135deg, #106070 0%, #00A6B5 100%)',
      border: '#00A6B5',
      accent: '#F5C842',
      badgeColor: '#FFE07A',
      playBtn: '#F5C842',
      playFg: '#5C4A1A',
      shadow: '0 6px 18px rgba(0, 166, 181, 0.3)',
      titleAccent: '#9CDDE2',
    },
    omok /* V70G6B — 엔글 오목 (딥 포레스트 그린 + 샌드) */: {
      gradient: 'linear-gradient(135deg, #052e2b 0%, #065f46 100%)',
      border: '#10b981',
      accent: '#fbbf24',
      badgeColor: '#fcd34d',
      playBtn: '#fbbf24',
      playFg: '#422006',
      shadow: '0 6px 18px rgba(6, 95, 70, 0.3)',
      titleAccent: '#a7f3d0',
    },
  }[p.theme];
  const viewStates = p.blockedViewStates ?? DEFAULT_BLOCKED_VIEW_STATES;
  const showBlockedView = !p.canPlay && !!p.period && viewStates.includes(p.period.state) && !!p.blockedViewAction;
  // V70-J — 타자왕 카드만 적용 (마블·오목 카드는 기존 배치 그대로)
  const compact = !!p.compactActions;
  // V70-O — 마블 등 비컴팩트 카드의 소진 상태: [막힌 버튼 + 보기]를 기존 게임시작 영역(flex 2) 안에 나눠 배치.
  //   이전엔 두 버튼(flex 1 + 1.4, nowrap, min-width 기본값)이 리더보드 몫까지 차지해 리더보드가 줄 밖으로 밀렸음.
  const splitPlayArea = showBlockedView && !compact;

  return (
    <div style={{
      background: themeMap.gradient,
      border: `1px solid ${themeMap.border}`,
      borderRadius: 16,
      padding: 20,
      display: 'flex',
      flexDirection: 'column',
      gap: 14,
      boxShadow: themeMap.shadow,
      minHeight: 380,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ fontSize: 11, color: themeMap.badgeColor, letterSpacing: 2, fontWeight: 700 }}>
          {p.badge}
        </div>
        {p.period && (
          <PeriodChip period={p.period} />
        )}
      </div>
      <h2 style={{ fontSize: 24, fontWeight: 800, color: '#f1f5f9', margin: 0, lineHeight: 1.2 }}>
        <span style={{ color: themeMap.accent }}>엔글</span>
        <span style={{ color: '#f0f0f0' }}> </span>
        <span style={{ color: themeMap.titleAccent }}>{p.titleAccent}</span>
      </h2>

      {/* 일러스트 */}
      <div style={{
        aspectRatio: '16/9',
        background: 'rgba(255,255,255,0.03)',
        border: '1px dashed rgba(255,255,255,0.08)',
        borderRadius: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}>
        {p.illustration}
      </div>

      <p style={{ color: '#cbd5e1', fontSize: 13, margin: 0, lineHeight: 1.6, flex: 1 }}>
        {p.summary}
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {p.metaChips.map((m, i) => (
          <span key={i} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '3px 8px',
            background: 'rgba(148, 163, 184, 0.1)',
            border: '1px solid rgba(148, 163, 184, 0.2)',
            borderRadius: 10,
            color: '#cbd5e1',
            fontSize: 11,
            fontWeight: 500,
          }}>
            {m.icon} {m.label}
          </span>
        ))}
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 10px',
        background: 'rgba(0,0,0,0.2)',
        borderRadius: 8,
      }}>
        <span style={{ fontSize: 11, color: p.statColors?.label ?? '#94a3b8' }} data-testid="game-card-stat-label">{p.statLabel}</span>
        <span style={{ fontSize: 16, fontWeight: 800, color: p.statColors?.value ?? (p.statActive ? themeMap.accent : '#64748b') }} data-testid="game-card-stat-value">
          {p.statValue}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 'auto', ...(compact ? COMPACT_ROW : null) }} data-testid="game-card-actions">
        <PlayArea split={splitPlayArea}>
        <button
          disabled={!p.canPlay}
          onClick={p.canPlay ? p.onPlay : undefined}
          data-testid="game-card-play"
          style={{
            flex: showBlockedView ? 1 : 2,
            whiteSpace: 'nowrap',
            padding: `12px ${PLAY_BTN_PAD_X}px`,
            background: p.canPlay ? themeMap.playBtn : '#334155',
            color: p.canPlay ? themeMap.playFg : '#94a3b8',
            fontWeight: 700,
            fontSize: 14,
            border: 0,
            borderRadius: 8,
            cursor: p.canPlay ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            boxShadow: p.canPlay ? '0 0 12px rgba(255,255,255,0.08)' : 'none',
            ...(compact ? (showBlockedView ? COMPACT_PLAY : COMPACT_PLAY_SOLO) : null),
            ...(splitPlayArea ? SPLIT_BLOCKED : null),
          }}
          title={p.period?.reason ?? (splitPlayArea ? p.blockedDuringLabel : undefined) ?? undefined}
        >
          {p.canPlay ? <><Play size={16} /> 게임 시작</> :
           p.period?.state === 'before' ? <><Lock size={16} /> 오픈 전</> :
           p.period?.state === 'after' ? <>🏁 종료됨</> :
           p.period?.state === 'disabled' ? <><Lock size={16} /> 비공개</> :
           /* ISSUE-02 재제보: period 가 공개 중(during)인데 canPlay=false → 코인/횟수 소진.
              호출부가 blockedDuringLabel 명시 시 그 라벨 사용 (예: 타자왕 '내일 재도전').
              미명시 또는 period 자체가 null 인 fallback 은 '비공개' 로 두지 않고 '잠시 후 다시' 사용. */
           p.period?.state === 'during' && p.blockedDuringLabel
             ? (splitPlayArea
               // V70-O — 좁은 묶음 영역에서는 글자가 잘리지 않도록 아이콘 생략 (긴 라벨만 말줄임 + 툴팁)
               ? <span style={ELLIPSIS}>{p.blockedDuringLabel}</span>
               : <>{p.blockedDuringIcon ?? <Lock size={16} />} {p.blockedDuringLabel}</>)
             : p.period?.state === 'during'
             ? <><Lock size={16} /> 잠시 후 다시</>
             : <><Lock size={16} /> 비공개</>}
        </button>
        {showBlockedView && p.blockedViewAction && (
          <button
            onClick={p.blockedViewAction.onClick}
            data-testid="game-card-view-action"
            style={{
              flex: 1.4,
              padding: '12px 14px',
              background: themeMap.playBtn,
              color: themeMap.playFg,
              fontWeight: 700,
              fontSize: 14,
              border: 0,
              borderRadius: 8,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              whiteSpace: 'nowrap',
              ...(compact ? COMPACT_VIEW : null),
              ...(splitPlayArea ? SPLIT_VIEW : null),
            }}
            title={splitPlayArea ? p.blockedViewAction.label : undefined}
          >
            {splitPlayArea
              ? <span style={ELLIPSIS}>{p.blockedViewAction.label}</span>
              : <><Eye size={compact ? 14 : 16} /> {p.blockedViewAction.label}</>}
          </button>
        )}
        </PlayArea>
        {/* V70G6E — secondaryAction 지정 시 리더보드 대신 그 액션 노출 (오목 경기 규칙). */}
        {p.secondaryAction ? (
          <button
            onClick={p.secondaryAction.onClick}
            style={{
              flex: 1,
              padding: '12px 14px',
              background: 'transparent',
              color: '#cbd5e1',
              border: '1px solid #475569',
              borderRadius: 8,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              fontSize: 13,
            }}
            data-testid="game-card-secondary-action"
          >
            {p.secondaryAction.icon === 'rules'
              ? <><BookOpen size={14} /> {p.secondaryAction.label} <ChevronRight size={14} /></>
              : <><Trophy size={14} /> {p.secondaryAction.label} <ChevronRight size={14} /></>}
          </button>
        ) : (
          <button
            onClick={p.onLeaderboard}
            style={{
              flex: 1,
              padding: '12px 14px',
              background: 'transparent',
              color: '#cbd5e1',
              border: '1px solid #475569',
              borderRadius: 8,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              fontSize: 13,
              ...(compact ? COMPACT_LEADERBOARD : null),
            }}
            data-testid="game-card-leaderboard"
          >
            <Trophy size={14} /> 리더보드 <ChevronRight size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

// ──────── 상태 뱃지 (COMING SOON / 진행 중 / ENDED / 비공개) ────────

function PeriodChip({ period }: { period: GamePeriodResult }) {
  const b = period.isAlwaysOpen ? ALWAYS_OPEN_BADGE : periodBadge(period.state);
  const label = (() => {
    if (period.isAlwaysOpen) return ALWAYS_OPEN_BADGE.label;
    // ISSUE-06: before/during 모두 formatDiff 로 통일 — 24h 이상 D-N, 1h 미만은 분/1분미만.
    if (period.state === 'before' && period.diffMs) return `${b.label} · 오픈까지 ${formatDiff(period.diffMs)}`;
    if (period.state === 'during' && period.diffMs) return `${b.label} · 종료 ${formatDiff(period.diffMs)} 전`;
    return b.label;
  })();
  return (
    <span style={{ padding: '3px 10px', borderRadius: 12, background: b.bg, color: b.fg, fontSize: 10, fontWeight: 800, letterSpacing: 0.5 }}>
      {label}
    </span>
  );
}

// ──────── 일러스트 (SVG, 외부 CDN X) ────────

// V70 디자인 고급화 — 미니멀 프리미엄 아케이드 (차콜/블루그레이/샴페인 골드).
// 네온 cyan/빨간 BASE 라인 제거, 골드 포인트로 통일.
function AcidRainIllust() {
  const words = ['사과', '단어', '엔글', '타자', '산성', '게임'];
  return (
    <svg viewBox="0 0 240 135" width="100%" height="100%" style={{ maxHeight: 180 }}>
      <defs>
        <linearGradient id="arBg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={ACIDRAIN_COLORS.bgPrimary} />
          <stop offset="1" stopColor={ACIDRAIN_COLORS.panel} />
        </linearGradient>
        <linearGradient id="arTrail" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={ACIDRAIN_COLORS.gold} stopOpacity="0.55" />
          <stop offset="1" stopColor={ACIDRAIN_COLORS.gold} stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="240" height="135" fill="url(#arBg)" />
      {[20, 55, 90, 130, 170, 210].map((x, i) => (
        <g key={i} transform={`translate(${x} ${i % 2 === 0 ? 10 : 25})`}>
          <rect
            x="-18" y="0" width="36" height="18" rx="4"
            fill={ACIDRAIN_COLORS.panelDeep}
            stroke={ACIDRAIN_COLORS.gold}
            strokeOpacity="0.65"
            strokeWidth="1"
          />
          <text x="0" y="13" fontSize="10" textAnchor="middle" fill={ACIDRAIN_COLORS.text} fontFamily="monospace">
            {words[i]}
          </text>
          <line x1="0" y1="20" x2="0" y2="100" stroke="url(#arTrail)" strokeWidth="1.5" strokeDasharray="3 5" />
        </g>
      ))}
      <line x1="0" y1="128" x2="240" y2="128" stroke={ACIDRAIN_COLORS.danger} strokeOpacity="0.75" strokeWidth="2" />
      <text x="120" y="125" fontSize="9" textAnchor="middle" fill={ACIDRAIN_COLORS.danger} fontWeight="700" letterSpacing="1">
        BASE
      </text>
    </svg>
  );
}

// V70 2026-10-19 — NGLE 브랜드 Teal 재디자인 (지시 §6 — 게임센터 썸네일 통일).
//   파스텔 teal 바탕 + Primary Teal (#00A6B5) 액센트 + Gold (#F5C842) START + 다크 다이스.
function MarbleIllust() {
  const cellPositions = [
    { x: 20, y: 15 }, { x: 60, y: 15 }, { x: 100, y: 15 }, { x: 140, y: 15 }, { x: 180, y: 15 },
    { x: 180, y: 55 }, { x: 180, y: 95 },
    { x: 140, y: 95 }, { x: 100, y: 95 }, { x: 60, y: 95 }, { x: 20, y: 95 },
    { x: 20, y: 55 },
  ];
  const cellFill = (i: number) => {
    if (i === 0) return '#F5C842';                   // START = Gold
    if (i === 6) return '#00A6B5';                   // 반대 코너 = Primary Teal
    if (i % 5 === 0) return '#9CDDE2';               // 액센트 = Light Teal
    return '#FFFFFF';                                // 일반 칸 = 화이트
  };
  return (
    <svg viewBox="0 0 240 135" width="100%" height="100%" style={{ maxHeight: 180 }}>
      <defs>
        <linearGradient id="mbBoard" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#DFF2F4" />
          <stop offset="0.5" stopColor="#FFFFFF" />
          <stop offset="1" stopColor="#C8E6E6" />
        </linearGradient>
      </defs>
      <rect x="20" y="15" width="200" height="105" rx="8" fill="url(#mbBoard)" stroke="#00A6B5" strokeWidth="2" />
      {cellPositions.map((pt, i) => (
        <rect
          key={i}
          x={pt.x} y={pt.y} width="40" height="25" rx="3"
          fill={cellFill(i)}
          stroke="#00A6B5"
          strokeOpacity="0.4"
          strokeWidth="0.5"
        />
      ))}
      <text x="120" y="63" fontSize="16" fontWeight="700" textAnchor="middle" fill="#106070" letterSpacing="1">
        엔글 마블
      </text>
      {/* 다이스 — 흰색 바탕 + Primary Teal pip */}
      <rect x="112" y="72" width="16" height="16" rx="3" fill="#FFFFFF" stroke="#00A6B5" strokeWidth="1" />
      <circle cx="117" cy="77" r="1.2" fill="#00A6B5" />
      <circle cx="123" cy="77" r="1.2" fill="#00A6B5" />
      <circle cx="120" cy="80" r="1.2" fill="#00A6B5" />
      <circle cx="117" cy="83" r="1.2" fill="#00A6B5" />
      <circle cx="123" cy="83" r="1.2" fill="#00A6B5" />
    </svg>
  );
}

// V70G6B — 엔글 오목 일러스트 (15x15 보드 + 흑백 돌 몇 개).
function OmokIllust() {
  return (
    <svg viewBox="0 0 240 135" width="100%" height="100%" style={{ maxHeight: 180 }}>
      <rect x="20" y="15" width="200" height="105" rx="8" fill="#f3e3c0" stroke="#5a3f1a" strokeWidth="2" />
      {/* 격자 */}
      {Array.from({ length: 10 }, (_, i) => (
        <g key={`vl-${i}`}>
          <line x1={30 + i * 18} y1="25" x2={30 + i * 18} y2="110" stroke="#5a3f1a" strokeWidth="0.5" />
        </g>
      ))}
      {Array.from({ length: 6 }, (_, i) => (
        <g key={`hl-${i}`}>
          <line x1="30" y1={25 + i * 17} x2="210" y2={25 + i * 17} stroke="#5a3f1a" strokeWidth="0.5" />
        </g>
      ))}
      {/* 흑백 돌 샘플 */}
      <circle cx="84" cy="59" r="7" fill="#1a1a1a" stroke="#000" />
      <circle cx="102" cy="59" r="7" fill="#1a1a1a" stroke="#000" />
      <circle cx="120" cy="59" r="7" fill="#f5f5f5" stroke="#888" />
      <circle cx="120" cy="76" r="7" fill="#f5f5f5" stroke="#888" />
      <circle cx="138" cy="59" r="7" fill="#1a1a1a" stroke="#000" />
      <circle cx="156" cy="76" r="7" fill="#f5f5f5" stroke="#888" />
      <circle cx="138" cy="76" r="7" fill="#1a1a1a" stroke="#000" />
      {/* 액센트 — 마지막 수 하이라이트 */}
      <circle cx="138" cy="76" r="3.5" fill="none" stroke="#ef4444" strokeWidth="1.5" />
    </svg>
  );
}

// ──────── 스타일 ────────
// (demoBanner 는 Phase 2 문구 제거와 함께 삭제됨 — 각 게임에서 로컬 안내 제공)

// V70G6E — 3-카드 반응형 레이아웃.
//   데스크톱 (1200px+): 3열 (minmax 280px → 3개 nicely fit in 1400px parent)
//   태블릿 (720-1199px): 2열
//   모바일 (<720px): 1열
//   gap 16px (기존 20 → 축소), minmax 280 (기존 300 → 축소) 로 좁은 화면에서도 가독성 유지.
const cardsGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: 16,
  marginBottom: 24,
};

// V70G6F — 하단 리더보드 2열 레이아웃 (타자왕 50% / 마블 50%).
//   PC (≥720px): 2열, 모바일 (<720px): 1열 세로 배치.
//   auto-fit minmax 로 자연 반응형. gap 16 (상단 cardsGrid 와 통일).
const leaderboardsGrid: React.CSSProperties = {
  display: 'grid',
  // V70-K — 본문이 320px 보다 좁은 모바일에서 가로 넘침 방지 (넓은 화면 배치는 동일)
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))',
  gap: 16,
  marginBottom: 24,
};

// (deprecated — V70G6F 레이아웃 변경 후 columnStyle 미사용. 기존 사용처 제거됨.)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const columnStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};

// V70-M — TOP 10 카드: 다크 섹션 카드
const lbSectionStyle: React.CSSProperties = {
  ...darkCard,
  padding: 16,
};

const adminPreviewStyle: React.CSSProperties = {
  ...darkTintBadge('#6366f1'),
  padding: '6px 10px',
  borderRadius: 6,
  fontSize: 12,
  marginBottom: 8,
};

const errorBoxStyle: React.CSSProperties = {
  background: darkErrorBox.background,
  border: darkErrorBox.border,
  color: darkErrorBox.color,
  padding: '10px 12px',
  borderRadius: 8,
  fontSize: 12,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
};

const retryBtnStyle: React.CSSProperties = {
  marginLeft: 'auto',
  padding: '4px 10px',
  background: DARK.surface,
  border: '1px solid rgba(239, 68, 68, 0.45)',
  color: '#FCA5A5',
  borderRadius: 6,
  fontSize: 11,
  cursor: 'pointer',
  fontWeight: 600,
};

const lbThStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  color: DARK.textMuted,
  fontSize: 11,
  fontWeight: 600,
  borderBottom: `1px solid ${DARK.border}`,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};

const lbTdStyle: React.CSSProperties = {
  padding: '10px',
  borderBottom: `1px solid ${DARK.border}`,
  fontSize: 13,
  color: DARK.text,
};

const seeMoreBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: 0,
  color: '#8CC8FF',
  fontSize: 12,
  cursor: 'pointer',
};

export default TournamentGameCenterPage;
